import { array, option } from 'fp-ts';
import { flow } from 'fp-ts/function';
import { CurrencyAcceptance } from '@mews-commander/data-access';
import {
    multiplyNumbersAsDecimals,
    NumberFormatting,
    NumberService,
    sumMultipleNumbersAsDecimals,
} from '@mews/framework';
import { Entities } from '@mews-commander/legacy-entities/src/ducks';
import * as fieldNames from './constants';
import { ProductFieldNameResolverFn, ProductUnits, ValidatedCurrencyProductUnits } from './types';

const getProductUnitFieldName = (productId: string, fieldName: string) =>
    `${fieldNames.UNITS}.${productId}.${fieldName}`;

export const getProductUnitCostFieldName: ProductFieldNameResolverFn = (productId) =>
    getProductUnitFieldName(productId, fieldNames.UNIT_COST);

export const getProductUnitAmountFieldName: ProductFieldNameResolverFn = (productId) =>
    getProductUnitFieldName(productId, fieldNames.UNIT_AMOUNT);

export const mapCostToDefaultCurrency = (
    exchangeRates: Entities<CurrencyAcceptance>,
    numberLocalizationInfo: NumberFormatting
) =>
    array.filterMap(({ unitCost = '0', unitCurrency, ...rest }: ValidatedCurrencyProductUnits) => {
        const currentExchangeRate = exchangeRates[unitCurrency];
        if (!currentExchangeRate) return option.none;

        let resultCostToExchange = unitCost;
        const parsedCostToExchange = NumberService.unformat(unitCost, numberLocalizationInfo);

        if (parsedCostToExchange) {
            const result = multiplyNumbersAsDecimals(parsedCostToExchange, currentExchangeRate.conversionRateToDefault);

            resultCostToExchange = NumberService.format(result, numberLocalizationInfo);
        }

        return option.some({
            ...rest,
            unitCurrency,
            unitCost: resultCostToExchange,
        });
    });

const getAllUnitsTotalCost = (currentNumberCultureInfo: NumberFormatting) =>
    array.map(({ unitCost, unitAmount }: ValidatedCurrencyProductUnits) => {
        const unformatCost = NumberService.unformat(unitCost, currentNumberCultureInfo);
        const parsedAmount = parseInt(unitAmount, 10);

        return multiplyNumbersAsDecimals(unformatCost, parsedAmount);
    });

export const getAllProductUnitsSum = (currentNumberCultureInfo: NumberFormatting) =>
    flow(getAllUnitsTotalCost(currentNumberCultureInfo), sumMultipleNumbersAsDecimals);

export const getCurrencyArray = (productUnits: ProductUnits[]) => {
    const unitsCurrencyList = array.map(({ unitCurrency }: ProductUnits) => unitCurrency)(productUnits);

    return [...new Set([...unitsCurrencyList])];
};

const sanitizeProductUnitValue = (value: string) => {
    if (Number.isNaN(Number.parseFloat(value))) {
        return '0';
    }

    return value;
};

export const sanitizeProductUnitsValues = array.map<ProductUnits, ValidatedCurrencyProductUnits>(
    ({ unitAmount = '0', unitCost = '0', ...rest }) => {
        const sanitizedCost = sanitizeProductUnitValue(unitCost);
        const sanitizedAmount = sanitizeProductUnitValue(unitAmount);

        return {
            unitCost: sanitizedCost,
            unitAmount: sanitizedAmount,
            ...rest,
        };
    }
);
